import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  CheckCircle, XCircle, AlertCircle, Loader2, ShieldAlert, Wrench,
  Building, User, Calendar, Clock, DollarSign, Bot, Phone, Mail,
  Hash, Tag, ExternalLink, ImageIcon, HardHat,
} from 'lucide-react';
import maintenanceService from '../services/maintenanceService';

// ── Helpers ────────────────────────────────────────────────────────────────

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'Emergency': return 'bg-red-100 text-red-800 border-red-200';
    case 'High':      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'Medium':    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Low':       return 'bg-green-100 text-green-800 border-green-200';
    default:          return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Open':             return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'In Progress':      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'Pending Approval': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'Scheduled':        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
    case 'Resolved':         return 'bg-green-100 text-green-800 border-green-200';
    case 'Closed':           return 'bg-gray-100 text-gray-800 border-gray-200';
    default:                 return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-muted-foreground text-xs">{label}</span>
        <p className="font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────

const MaintenanceApproval = ({ user }) => {
  const { caseId } = useParams();
  const [maintenanceCase, setMaintenanceCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState(null);
  const isLandlord = user?.userType === 'Landlord';

  useEffect(() => {
    if (!isLandlord) return;
    const fetchCase = async () => {
      try {
        setLoading(true);
        const data = await maintenanceService.getById(caseId);
        setMaintenanceCase(data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load maintenance case. You may not have permission to view it.');
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [caseId, isLandlord]);

  // Non-Landlord guard
  if (!isLandlord) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center gap-4">
              <ShieldAlert className="h-12 w-12 text-red-500" />
              <h2 className="text-xl font-semibold">Access Denied</h2>
              <p className="text-gray-500">Only Landlord accounts can review and approve maintenance requests.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleApproval = async (action) => {
    if (action === 'reject' && !reason.trim()) {
      setError('Please provide a reason for rejection.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const data = await maintenanceService.submitApproval(caseId, action, reason.trim() || undefined);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit approval action.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !maintenanceCase) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center gap-4">
              <AlertCircle className="h-12 w-12 text-red-500" />
              <h2 className="text-xl font-semibold">Error</h2>
              <p className="text-gray-500">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state after submitting
  if (result) {
    const isApproved = result.approvalStatus === 'approved';
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center gap-4">
              {isApproved
                ? <CheckCircle className="h-14 w-14 text-green-500" />
                : <XCircle    className="h-14 w-14 text-red-500" />}
              <h2 className="text-xl font-semibold">
                Request {isApproved ? 'Approved' : 'Rejected'}
              </h2>
              <p className="text-gray-500">{result.message}</p>
              <Link
                to={`/maintenance?case=${caseId}`}
                className="inline-flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                View maintenance record
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already decided state
  if (maintenanceCase.approvalStatus === 'approved' || maintenanceCase.approvalStatus === 'rejected') {
    const isApproved = maintenanceCase.approvalStatus === 'approved';
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center gap-4">
              {isApproved
                ? <CheckCircle className="h-14 w-14 text-green-500" />
                : <XCircle    className="h-14 w-14 text-red-500" />}
              <h2 className="text-xl font-semibold">Already Responded</h2>
              <p className="text-gray-500">
                This request has already been <strong>{maintenanceCase.approvalStatus}</strong>.
              </p>
              <Link
                to={`/maintenance?case=${caseId}`}
                className="inline-flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                View maintenance record
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Derive photo attachments ───────────────────────────────────────────
  const photos = (maintenanceCase.attachments || []).filter(
    a => a.fileType === 'image' || /\.(jpg|jpeg|png|webp|gif)$/i.test(a.fileUrl || '')
  );

  const tenantPhone = maintenanceCase.tenant?.phone;
  const tenantEmail = maintenanceCase.tenant?.email;

  // ── Main approval page ────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-5">

      {/* Page title + MR link */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Wrench className="h-5 w-5 text-amber-500" />
          Approval Required
        </h1>
        <Link
          to={`/maintenance?case=${caseId}`}
          className="inline-flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View full record
        </Link>
      </div>

      {/* ── Case header ── */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground font-mono mb-1">{maintenanceCase.caseNumber}</p>
              <CardTitle className="text-base leading-snug">{maintenanceCase.title}</CardTitle>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(maintenanceCase.status)}`}>
              {maintenanceCase.status}
            </span>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getPriorityColor(maintenanceCase.priority)}`}>
              {maintenanceCase.priority} Priority
            </span>
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-gray-50 text-gray-700 border-gray-200">
              <Tag className="h-3 w-3 mr-1" />
              {maintenanceCase.category}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">

          {/* Details grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
            <DetailRow icon={Building} label="Property"  value={maintenanceCase.property?.name} />
            {maintenanceCase.unitNumber && (
              <DetailRow icon={Hash} label="Unit" value={`Unit ${maintenanceCase.unitNumber}`} />
            )}
            {maintenanceCase.tenant?.name && (
              <DetailRow icon={User} label="Tenant" value={maintenanceCase.tenant.name} />
            )}
            {tenantPhone && (
              <DetailRow icon={Phone} label="Phone" value={tenantPhone} />
            )}
            {tenantEmail && (
              <DetailRow icon={Mail} label="Email" value={tenantEmail} />
            )}
            <DetailRow
              icon={Clock}
              label="Submitted"
              value={new Date(maintenanceCase.createdAt).toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' })}
            />
            {maintenanceCase.scheduledDate && (
              <DetailRow
                icon={Calendar}
                label="Scheduled"
                value={new Date(maintenanceCase.scheduledDate).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              />
            )}
          </div>

          {/* Estimated cost */}
          {maintenanceCase.estimatedCost > 0 && (
            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <DollarSign className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-xs text-amber-600">Estimated Cost</p>
                <p className="text-lg font-bold text-amber-800">${maintenanceCase.estimatedCost.toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Service provider */}
          {maintenanceCase.assignedTo?.name && (
            <div className="flex items-center gap-2 rounded-lg border bg-gray-50 px-4 py-3 text-sm">
              <HardHat className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Assigned Provider</p>
                <p className="font-medium text-gray-800">{maintenanceCase.assignedTo.name}</p>
                {maintenanceCase.assignedTo.contactInfo && (
                  <p className="text-xs text-muted-foreground">{maintenanceCase.assignedTo.contactInfo}</p>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Description</p>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {maintenanceCase.description}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── AI Conversation Summary ── */}
      {maintenanceCase.aiSummary && (
        <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-5 shadow-sm">
          <h3 className="font-semibold text-violet-800 mb-2 flex items-center gap-2 text-sm">
            <Bot className="h-4 w-4 text-violet-600" />
            AI Conversation Summary
            <span className="ml-auto text-[10px] font-normal text-violet-600 bg-violet-100 border border-violet-200 rounded-full px-2 py-0.5">
              Niva AI
            </span>
          </h3>
          <p className="text-sm text-violet-900 leading-relaxed">{maintenanceCase.aiSummary}</p>
        </div>
      )}

      {/* ── Photos ── */}
      {photos.length > 0 && (
        <Card className="shadow-sm">
          <CardContent className="pt-4 pb-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
              Photos
              <span className="text-xs text-muted-foreground font-normal">({photos.length})</span>
            </h3>
            <div className="flex flex-wrap gap-3">
              {photos.map((img, i) => (
                <a
                  key={i}
                  href={img.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative h-24 w-24 rounded-lg overflow-hidden border border-gray-200 hover:border-cyan-400 transition-colors flex-shrink-0"
                >
                  <img
                    src={img.fileUrl}
                    alt={img.fileName || `Photo ${i + 1}`}
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-200"
                  />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Approval form ── */}
      <Card className="border-amber-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-amber-800 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            Your Decision
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            A reason is required when rejecting. Optionally add notes when approving.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm border border-red-200">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="reason">Reason / Notes</Label>
            <Textarea
              id="reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required if rejecting. Optional notes for approval..."
              className="mt-1"
            />
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => handleApproval('approve')}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <CheckCircle className="h-4 w-4 mr-2" />}
              Approve
            </Button>
            <Button
              onClick={() => handleApproval('reject')}
              disabled={submitting}
              variant="destructive"
              className="flex-1 sm:flex-none"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <XCircle className="h-4 w-4 mr-2" />}
              Reject
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default MaintenanceApproval;
